import React, { useState, useCallback, useContext, useMemo, useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import useHelpDeskTicket from '@ubeya/shared/hooks/account/useHelpDeskTicket';
import useHelpDeskMessages from '@ubeya/shared/hooks/account/useHelpDeskMessages';
import useHelpDeskTickets from '@ubeya/shared/hooks/account/useHelpDeskTickets';
import useHelpDeskSendMessage from '@ubeya/shared/hooks/account/useHelpDeskSendMessage';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { LoaderArea, CircleIcon, EmptyState, FiltersRow, PdfModal } from '@ubeya/shared-web/components';
import { Avatar } from '@ubeya/shared-web/components/Avatar';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import usePortal from '@ubeya/shared-web/hooks/usePortal';
import { ReactComponent as FullScreenOpenIcon } from '@ubeya/shared-web/assets/full-screen-open.svg';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import { helpDeskSidePanel, helpDeskTicketsListState } from '@ubeya/shared/atoms/shared';
import { HELP_DESK_DEFAULT_STATUS, HELP_DESK_DEFAULT_SORT, HELP_DESK_STATUSES } from '@ubeya/shared/constants/helpDesk';
import { ReactComponent as ArrowLeftIcon } from '@ubeya/shared-web/assets/arrow-left.svg';
import { ReactComponent as PlusIcon } from '@ubeya/shared-web/assets/help-desk/plus.svg';
import useDebounce from '@ubeya/shared/hooks/useDebounce';
import { groupMessagesByDate, extractEmployeeInfo, getTicketBranch } from '../helpDeskUtils';
import useHelpDeskSocket from '../useHelpDeskSocket';
import useHelpDeskFilters from '../hooks/useHelpDeskFilters';
import useHelpDeskTicketActions from '../useHelpDeskTicketActions';
import useMarkAsRead from '../useMarkAsRead';
import useAutoScrollToBottom from '../useAutoScrollToBottom';
import useOpenTicketInSidePanel from '../useOpenTicketInSidePanel';
import { useHelpDeskAnalytics } from '../helpDeskAnalytics';
import HelpDeskTicketMetadata, { HelpDeskCategoryConfirmModal } from './HelpDeskTicketMetadata';
import HelpDeskComposer from './HelpDeskComposer';
import HelpDeskTicketActions from './HelpDeskTicketActions';
import {
  MessagesList,
  BranchChip,
  BranchDisplay,
  EventLink,
  HelpDeskTabs,
  HelpDeskTicketsToolbar,
  HelpDeskTicketsList,
  HelpDeskTicketStatusBanners,
  CreateFab,
  ResolvedBadge,
  chipsRowStyle,
  TicketBadge
} from './shared';
import CreateTicketModal from './CreateTicket/CreateTicketModal';

// region: Layout

const jumpIn = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(0); }
`;

const Panel = styled.div`
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: -10px 4px 30px 0 rgba(12, 27, 54, 0.1);
  animation: ${jumpIn} 300ms;
  z-index: 110;
  display: flex;
  flex-direction: column;
`;

const PortalZIndexFix = createGlobalStyle`
  #select.portal-select-input,
  #modal {
    position: relative;
    z-index: 120 !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  gap: 6px;
  min-height: 56px;
`;

const PanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

// region: Tabs + Filters

const PanelFiltersChipsRow = styled.div`
  padding: 0 20px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey1};
  flex-shrink: 0;
`;

// region: Tickets list

const TicketsState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// region: Conversation - ticket header

const ConvTicketHeader = styled.div`
  padding: 19px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  display: flex;
  flex-direction: column;
`;

const ConvHeaderContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ConvAvatarWrap = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
`;

const ConvContentWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConvTicketTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 28px;
`;

const ConvEmployeeName = styled.div`
  font-size: 16px;
  line-height: 120%;
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConvActionsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-inline-start: auto;
`;

const ConvSubjectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ConvTicketTitle = styled.div`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ConvTicketNumber = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ConvMetaRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 11px;
  margin-left: -8px;
  ${chipsRowStyle}
`;

const CloseButtonWrapper = styled.div`
  margin-left: auto;
`;

// region: Messages

const ConversationContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.grey1};
`;

const LoadEarlierButton = styled.button`
  display: block;
  margin: 0 auto 12px;
  padding: 6px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.grey1};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ComposerArea = styled.div`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.grey1};
`;

// region: Main component

const HelpDeskSidePanel = () => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedBranches } = useBranches();
  const history = useHistory();
  const { addResponseError } = useToaster();
  const [panelState, setPanelState] = useRecoilState(helpDeskSidePanel);
  const [listState, setListState] = useRecoilState(helpDeskTicketsListState);
  const viewFilter = listState.view;
  const setViewFilter = useCallback((value) => setListState((s) => ({ ...s, view: value })), [setListState]);
  const [statusFilter, setStatusFilter] = useState(HELP_DESK_DEFAULT_STATUS);
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState(HELP_DESK_DEFAULT_SORT);
  const [pdfUrl, setPdfUrl] = useState();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { filters, setFilters, disabledFilters, filterParams, noFilters } = useHelpDeskFilters(viewFilter);

  const debouncedSearch = useDebounce(searchText, 300);

  const { Portal } = usePortal({ enabled: true, portalId: 'modal' });

  const { isOpen, ticketId: selectedTicketId } = panelState;

  const close = useCallback(() => setPanelState({ isOpen: false, ticketId: null }), [setPanelState]);
  const goBack = useCallback(() => setPanelState((s) => ({ ...s, ticketId: null })), [setPanelState]);
  const openTicket = useCallback((id) => setPanelState((s) => ({ ...s, ticketId: id })), [setPanelState]);

  const { trackCreateTicketOpened } = useHelpDeskAnalytics();
  const handleOpenCreate = useCallback(() => {
    trackCreateTicketOpened('Help desk side panel');
    setIsCreateOpen(true);
  }, [trackCreateTicketOpened]);

  const openTicketInSidePanel = useOpenTicketInSidePanel();
  const handleTicketCreated = useCallback(
    (ticket) => {
      setIsCreateOpen(false);
      openTicketInSidePanel(ticket.id);
    },
    [openTicketInSidePanel]
  );
  const expandToFullView = useCallback(() => {
    if (selectedTicketId) {
      history.push(`/${accountId}/help-desk/${selectedTicketId}`);
    } else {
      history.push(`/${accountId}/help-desk`);
    }
    close();
  }, [selectedTicketId, accountId, history, close]);

  // Fetch tickets list
  const {
    tickets,
    tabCounts,
    total: ticketsTotal,
    isLoading: ticketsLoading,
    isFetchingNextPage: isFetchingNextTicketsPage,
    fetchNextPage: fetchNextTicketsPage,
    hasNextPage: hasNextTicketsPage
  } = useHelpDeskTickets({
    accountId,
    source: 'sidePanel',
    view: viewFilter,
    status: statusFilter,
    search: debouncedSearch || undefined,
    sort,
    ...filterParams,
    enabled: !selectedTicketId
  });

  // Fetch selected ticket detail
  const { ticket: ticketDetail, isLoading: ticketDetailLoading, isError: ticketNotVisible } = useHelpDeskTicket({
    accountId,
    ticketId: selectedTicketId,
    enabled: !!selectedTicketId
  });

  // Employee name + image come from the ticket detail response — no separate fetch needed.

  // Connect to help-desk WebSocket for real-time updates when viewing a ticket
  useHelpDeskSocket({ accountId, ticketId: selectedTicketId, enabled: isOpen && !!selectedTicketId });

  // Mark as read when opening a ticket
  useMarkAsRead({ accountId, ticketId: selectedTicketId });

  // Fetch selected ticket messages
  const { messages, isLoading: messagesLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useHelpDeskMessages({
    accountId,
    ticketId: selectedTicketId,
    enabled: !!selectedTicketId && !ticketNotVisible
  });

  // Group messages by date
  const groupedItems = useMemo(() => groupMessagesByDate([...messages].reverse()), [messages]);

  // Auto-scroll to bottom
  const { messagesEndRef } = useAutoScrollToBottom(groupedItems.length, selectedTicketId);

  // Send reply mutation
  const { sendMessageAsync, isLoading: isSending } = useHelpDeskSendMessage(
    { accountId, ticketId: selectedTicketId },
    { onError: addResponseError }
  );

  const handleSend = useCallback((body) => sendMessageAsync(body), [sendMessageAsync]);

  const conversationRef = useRef(null);
  const handleLoadEarlier = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = conversationRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    fetchNextPage().then(() => {
      requestAnimationFrame(() => {
        if (conversationRef.current) {
          conversationRef.current.scrollTop = conversationRef.current.scrollHeight - prevScrollHeight;
        }
      });
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isClosed = ticketDetail?.status === HELP_DESK_STATUSES.CLOSED;
  const isResolved = !!ticketDetail?.resolvedAt;

  // The ticket detail response carries the branch id only - the name and logo come from the branches mapper.
  const branch = getTicketBranch({ branchId: ticketDetail?.branchId, mappedBranches });

  // Shared ticket actions hook for category/priority/assignee changes
  const {
    categories: sidePanelCategories,
    admins: sidePanelAdmins,
    updateMutation,
    isEditable,
    pendingCategory,
    handleCategoryChange,
    confirmCategoryChange,
    cancelCategoryChange,
    handlePriorityChange,
    handleAssigneeChange
  } = useHelpDeskTicketActions(ticketDetail, accountId);

  // Employee data resolved from the ticket detail response — no separate fetch needed.
  const { employeeName, employeeAvatar, employeeFirstName, employeeLastName } = extractEmployeeInfo(ticketDetail);

  if (!isOpen) return null;

  return (
    <Portal>
      <PortalZIndexFix />
      <Panel>
        <PanelHeader>
          {selectedTicketId && <CircleIcon icon={ArrowLeftIcon} onClick={goBack} />}
          <PanelTitle>{t('help_desk')}</PanelTitle>
          <CircleIcon icon={FullScreenOpenIcon} onClick={expandToFullView} tooltip={t('help_desk_expand')} />
          <CloseButtonWrapper>
            <CircleIcon icon={CloseIcon} onClick={close} />
          </CloseButtonWrapper>
        </PanelHeader>

        {!selectedTicketId ? (
          <>
            <HelpDeskTabs value={viewFilter} onChange={setViewFilter} tabCounts={tabCounts} />

            <HelpDeskTicketsToolbar
              status={statusFilter}
              onStatusChange={setStatusFilter}
              total={ticketsTotal}
              sort={sort}
              onSortChange={setSort}
              filters={filters}
              setFilters={setFilters}
              disabledFilters={disabledFilters}
              search={searchText}
              onSearchChange={setSearchText}
            />

            {!noFilters && (
              <PanelFiltersChipsRow>
                <FiltersRow selectedKey="help-desk-tickets" filters={filters} setFilters={setFilters} usePortal />
              </PanelFiltersChipsRow>
            )}

            <MessagesContainer>
              <HelpDeskTicketsList
                tickets={tickets}
                isLoading={ticketsLoading}
                status={statusFilter}
                accountId={accountId}
                onTicketClick={openTicket}
                hasNextPage={hasNextTicketsPage}
                isFetchingNextPage={isFetchingNextTicketsPage}
                fetchNextPage={fetchNextTicketsPage}
              />
            </MessagesContainer>
          </>
        ) : (
          <>
            {ticketDetailLoading && (
              <TicketsState>
                <LoaderArea />
              </TicketsState>
            )}

            {!ticketDetailLoading && ticketDetail && (
              <>
                <ConvTicketHeader>
                  <ConvHeaderContentRow>
                    <ConvAvatarWrap>
                      <Avatar
                        firstName={employeeFirstName}
                        lastName={employeeLastName}
                        image={employeeAvatar}
                        size={50}
                      />
                    </ConvAvatarWrap>
                    <ConvContentWrap>
                      <ConvTicketTopRow>
                        <ConvEmployeeName>{employeeName}</ConvEmployeeName>
                        <TicketBadge ticket={ticketDetail} />
                        {isResolved && <ResolvedBadge />}

                        <ConvActionsArea>
                          <HelpDeskTicketActions ticket={ticketDetail} accountId={accountId} />
                        </ConvActionsArea>
                      </ConvTicketTopRow>

                      <ConvSubjectRow>
                        <ConvTicketTitle dir="auto">{ticketDetail.title || t('help_desk_ticket')}</ConvTicketTitle>
                        <ConvTicketNumber>#{ticketDetail.ticketNumber}</ConvTicketNumber>
                      </ConvSubjectRow>
                    </ConvContentWrap>
                  </ConvHeaderContentRow>

                  <ConvMetaRow>
                    {branch && (
                      <BranchChip $roundContent $readOnly>
                        <BranchDisplay branch={branch} />
                      </BranchChip>
                    )}

                    <HelpDeskTicketMetadata
                      ticket={ticketDetail}
                      categories={sidePanelCategories}
                      admins={sidePanelAdmins}
                      isEditable={isEditable}
                      isLoading={updateMutation.isLoading}
                      onCategoryChange={handleCategoryChange}
                      onPriorityChange={handlePriorityChange}
                      onAssigneeChange={handleAssigneeChange}>
                      <EventLink ticket={ticketDetail} accountId={accountId} showEmpty={true} />
                    </HelpDeskTicketMetadata>
                  </ConvMetaRow>
                </ConvTicketHeader>
                <HelpDeskTicketStatusBanners ticket={ticketDetail} accountId={accountId} />
              </>
            )}

            {!ticketDetailLoading && ticketNotVisible && (
              <TicketsState>
                <EmptyState text={t('help_desk_ticket_not_visible')} subText={t('help_desk_ticket_not_visible_sub')} />
              </TicketsState>
            )}

            {!ticketDetailLoading && !ticketNotVisible && (
              <>
                <ConversationContainer ref={conversationRef}>
                  {hasNextPage && (
                    <LoadEarlierButton onClick={handleLoadEarlier} disabled={isFetchingNextPage}>
                      {isFetchingNextPage ? t('loading') : t('help_desk_load_earlier')}
                    </LoadEarlierButton>
                  )}
                  <MessagesContainer>
                    <MessagesList
                      groupedItems={groupedItems}
                      accountId={accountId}
                      ticketId={selectedTicketId}
                      isLoading={messagesLoading}
                      onOpenPdf={setPdfUrl}
                      compact
                      messagesEndRef={messagesEndRef}
                    />
                  </MessagesContainer>
                </ConversationContainer>

                {ticketDetail && !isClosed && (
                  <ComposerArea>
                    <HelpDeskComposer
                      accountId={accountId}
                      ticketId={selectedTicketId}
                      ticket={ticketDetail}
                      onSend={handleSend}
                      isSending={isSending}
                    />
                  </ComposerArea>
                )}
              </>
            )}
          </>
        )}

        {!selectedTicketId && (
          <CreateFab onClick={handleOpenCreate} title={t('help_desk_create_new_ticket')}>
            <PlusIcon />
          </CreateFab>
        )}
      </Panel>

      <HelpDeskCategoryConfirmModal
        pendingCategory={pendingCategory}
        onConfirm={confirmCategoryChange}
        onClose={cancelCategoryChange}
      />

      {!!pdfUrl && <PdfModal url={pdfUrl} onClose={() => setPdfUrl(undefined)} />}

      {isCreateOpen && <CreateTicketModal onClose={() => setIsCreateOpen(false)} onCreated={handleTicketCreated} />}
    </Portal>
  );
};

export default HelpDeskSidePanel;
